/**
 * Inline metadata for a mixed-order fulfillment section header (order name, fulfilled date, Global-e id).
 */
import { observer } from 'mobx-react-lite';
import React from 'react';
import DateTimeV2, { DATETIME_VIEW } from '../../../components/datetime-v2/datetime-v2';
import FontBody3 from '../../../components/fonts/font-body3/font-body3';
import { FONT_COLOR } from '../../../components/fonts/font-props';
import useDashboardTranslation from '../../../dashboard-translation/hooks/dashboard-translation-hook';
import { useDateTimeFormattingParams } from '../../../hooks/use-datetime-formatting-params';
import type { CreateRmaDraftStore } from '../stores/create-rma-draft-store';

const PAGE_SCOPE = 'dashboard-create-rma';

export interface IFulfillmentSectionMetadataProps {
    draft: CreateRmaDraftStore;
}

const FulfillmentSectionMetadata: React.FC<IFulfillmentSectionMetadataProps> = observer(({ draft }) => {
    const { getTranslation } = useDashboardTranslation();
    const dateTimeParams = useDateTimeFormattingParams();
    const orderStore = draft.orderStore;
    const fulfilledDate = orderStore.getFulfilledDate();
    const daysSinceFulfillment = orderStore.getDaysSinceFulfillment();
    const glbeOrderId = orderStore.glbeOrderId;

    const metadata: React.ReactNode[] = [
        <>{getTranslation('ORDER', PAGE_SCOPE)} {draft.orderName}</>,
    ];

    if (fulfilledDate) {
        metadata.push(
            <>
                {getTranslation('FULFILLED', PAGE_SCOPE)}{' '}
                <DateTimeV2
                    {...dateTimeParams}
                    utcDateString={fulfilledDate}
                    dateTimeView={DATETIME_VIEW.DATE_ONLY}
                />
                {daysSinceFulfillment !== null && daysSinceFulfillment !== undefined
                    ? ` (${daysSinceFulfillment} ${getTranslation('DAYS_AGO', PAGE_SCOPE)})`
                    : ''}
            </>,
        );
    }

    if (glbeOrderId) {
        metadata.push(getTranslation('GLOBAL_E_ORDER_ID', PAGE_SCOPE, [glbeOrderId]));
    }

    return (
        <>
            {metadata.map((item, itemIndex) => (
                <FontBody3 key={itemIndex} color={FONT_COLOR.PRIMARY3_50}>
                    <span className="fulfillment-section-header__separator">·</span>
                    {item}
                </FontBody3>
            ))}
        </>
    );
});

export default FulfillmentSectionMetadata;
